import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useStepNavigate } from "../hooks/useStepNavigate";
import TablerLayout from "../components/layout/TablerLayout";
import Modal from "../components/modals/Modal";
import FloatingLabelInput from "../components/form_elements/FloatingLabelInput";
import dataService from "../services/dataService";
import { showErrorToast, showSuccessToast } from "../components/toasts/ToastService";

const MODEL_ID_FIELDS = ["szam", "id", "model_id", "modell_szam"];
const MODEL_NAME_FIELDS = ["name", "modell_name", "model_name"];
const MODEL_CODE_FIELDS = ["code", "modell_code", "model_code"];
const AIDFINANCE_COLUMNS = ["C0", "C2", "C3", "C4", "C5"];
const SPECIAL_NEED_COLUMNS = [
    { key: "is_open_nosed", label: "Nyitott orrú" },
    { key: "spn_nose_cutout", label: "Orrkivágás" },
    { key: "spn_hole_punch", label: "Díszlyukasztás" },
    { key: "spn_deco_stitch", label: "Díszöltés" },
    { key: "spn_tongue", label: "Nyelv" },
    { key: "spn_eyelet", label: "Fűzőszem" },
    { key: "spn_clasp", label: "Cipőszár kapcsozás" },
];
const AIDFINANCE_STATE_OPTIONS = [
    { value: "yes", label: "Igen", className: "btn-outline-success" },
    { value: "no", label: "Nem", className: "btn-outline-danger" },
    { value: "maybe", label: "Talán", className: "btn-outline-warning" },
];
const OPTIONAL_STATE_OPTIONS = [
    { value: "yes", label: "Igen", className: "btn-outline-success" },
    { value: "no", label: "Nem", className: "btn-outline-danger" },
    { value: "if", label: "Ha", className: "btn-outline-warning" },
];
const BOOLEAN_STATE_OPTIONS = [
    { value: 1, label: "Engedélyezve", className: "btn-outline-success" },
    { value: 0, label: "Tiltva", className: "btn-outline-danger" },
];
const BLOCKING_STATE_OPTIONS = [
    { value: 1, label: "Engedélyezve", className: "btn-outline-success" },
    { value: 0, label: "Tiltva", className: "btn-outline-danger" },
];
const COLOR_GROUP_COUNT = 3;

const normalizeApiPayload = (response) => response?.data?.data ?? response?.data ?? null;

const normalizeListPayload = (response) => {
    const payload = normalizeApiPayload(response);
    return Array.isArray(payload) ? payload : [];
};

const normalizeObjectPayload = (response) => {
    const payload = normalizeApiPayload(response);
    return payload && !Array.isArray(payload) ? payload : {};
};

const cloneValue = (value) => {
    if (Array.isArray(value) || (value && typeof value === "object")) {
        try {
            return JSON.parse(JSON.stringify(value));
        } catch (error) {
            return value;
        }
    }

    return value;
};

const parseJsonArray = (value) => {
    if (Array.isArray(value)) {
        return value;
    }

    if (typeof value !== "string" || value.trim() === "") {
        return [];
    }

    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return [];
    }
};

const normalizeComponentBlockedIds = (value) => {
    if (Array.isArray(value)) {
        return new Set(value.map((item) => String(item).trim()).filter(Boolean));
    }

    if (typeof value === "number") {
        return new Set([String(value)]);
    }

    if (typeof value !== "string" || value.trim() === "") {
        return new Set();
    }

    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
            return new Set(parsed.map((item) => String(item).trim()).filter(Boolean));
        }
    } catch (error) {
        // Fallback to loose numeric extraction below.
    }

    return new Set((value.match(/\d+/g) || []).map((item) => String(item).trim()));
};

const normalizeBlockedIdsList = (value) => {
    if (Array.isArray(value)) {
        return new Set(value.map((item) => String(item).trim()).filter(Boolean));
    }

    if (typeof value !== "string" || value.trim() === "") {
        return new Set();
    }

    return new Set(
        value
            .split(",")
            .map((item) => String(item).trim())
            .filter(Boolean)
    );
};

const getFirstDefinedValue = (item, fields, fallback = "—") => {
    for (const field of fields) {
        const value = item?.[field];
        if (value !== undefined && value !== null && value !== "") {
            return value;
        }
    }

    return fallback;
};

const getModelIndex = (model) => {
    const value = getFirstDefinedValue(model, MODEL_ID_FIELDS, null);
    if (value === null || value === undefined || value === "") {
        return null;
    }

    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? String(value).trim() : numericValue;
};

const getModelDisplayName = (model) => getFirstDefinedValue(model, MODEL_NAME_FIELDS, "Ismeretlen modell");

const getModelDisplayCode = (model) => getFirstDefinedValue(model, MODEL_CODE_FIELDS, "—");

const isModelHiddenInAdminList = (model) => {
    return Number(model?.hide_in_list) === 1;
};

const findMatchingConfig = (model, modelConfigs) => {
    const modelIndex = getModelIndex(model);
    if (modelIndex === null || !Array.isArray(modelConfigs)) {
        return null;
    }

    return modelConfigs.find((config) => Number(config?.mmelimodel) === Number(modelIndex)) || null;
};

const mergeArrayEntry = (entries, keyName, keyValue, nextEntry) => {
    const normalizedKey = String(keyValue).trim();
    const nextEntries = Array.isArray(entries) ? [...entries] : [];
    const existingIndex = nextEntries.findIndex((entry) => String(entry?.[keyName] ?? "").trim() === normalizedKey);

    if (existingIndex >= 0) {
        nextEntries[existingIndex] = { ...nextEntries[existingIndex], ...nextEntry };
        return nextEntries;
    }

    nextEntries.push(nextEntry);
    return nextEntries;
};

const removeSetValue = (sourceSet, value) => {
    const nextSet = new Set(sourceSet);
    nextSet.delete(String(value));
    return [...nextSet];
};

const addSetValue = (sourceSet, value) => {
    const nextSet = new Set(sourceSet);
    nextSet.add(String(value));
    return [...nextSet];
};

const toStemHeightValue = (value) => {
    const trimmedValue = String(value ?? "").trim();
    if (trimmedValue === "") {
        return null;
    }

    const numericValue = Number(trimmedValue);
    if (Number.isNaN(numericValue) || !Number.isInteger(numericValue)) {
        return null;
    }

    return numericValue;
};

const parseColorGroupsValue = (value) => {
    const normalizedGroups = Array.from({ length: COLOR_GROUP_COUNT }, () => []);

    if (typeof value !== "string" || value.trim() === "") {
        return normalizedGroups;
    }

    const groupParts = value.split(";").slice(0, COLOR_GROUP_COUNT);
    groupParts.forEach((groupText, groupIndex) => {
        const entries = String(groupText)
            .split(",")
            .map((entry) => entry.trim())
            .filter(Boolean);
        normalizedGroups[groupIndex] = entries;
    });

    return normalizedGroups;
};

const normalizeColorGroupsDraft = (groups) => {
    const sourceGroups = Array.isArray(groups) ? groups : [];

    return Array.from({ length: COLOR_GROUP_COUNT }, (_, groupIndex) => {
        const sourceEntries = Array.isArray(sourceGroups[groupIndex]) ? sourceGroups[groupIndex] : [];
        return sourceEntries.map((entry) => String(entry ?? "").trim()).filter(Boolean);
    });
};

const toColorGroupsValue = (groups) => {
    const normalizedGroups = normalizeColorGroupsDraft(groups);
    const hasAnyValue = normalizedGroups.some((groupEntries) => groupEntries.length > 0);

    if (!hasAnyValue) {
        return "";
    }

    return normalizedGroups.map((groupEntries) => groupEntries.join(",")).join(";");
};

const extractModelConfigIdFromResponse = (response, fallbackValue = null) => {
    const payload = normalizeApiPayload(response);

    const directCandidates = [
        payload?.mmelimodelconfig,
        payload?.mmelimodelconfig_szam,
        payload?.szam,
        payload?.id,
    ];

    for (const candidate of directCandidates) {
        if (candidate !== undefined && candidate !== null && candidate !== "") {
            if (typeof candidate === "object") {
                const nestedValue = candidate?.szam ?? candidate?.id;
                if (nestedValue !== undefined && nestedValue !== null && nestedValue !== "") {
                    return nestedValue;
                }
            } else {
                return candidate;
            }
        }
    }

    return fallbackValue;
};

const isErrorStatusResponse = (response) => {
    const payload = normalizeApiPayload(response);
    const status = String(payload?.status ?? response?.data?.status ?? "").trim().toLowerCase();
    return status === "error";
};

const ChoiceButtons = ({ options, value, onChange, disabled = false }) => {
    return (
        <div className="shadow-none btn-group btn-group-sm flex-wrap" role="group">
            {options.map((option) => {
                const isActive = String(option.value) === String(value);
                return (
                    <button
                        key={String(option.value)}
                        type="button"
                        className={`btn ${isActive ? option.className.replace("outline-", "") : option.className} m-1`}
                        onClick={() => onChange(option.value)}
                        disabled={disabled}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
};

const SectionCard = ({ title, description, children }) => (
    <div className="card h-100 shadow-sm">
        <div className="card-header">
            <div className="fw-semibold">{title}</div>
            {description ? <small className="text-muted ms-1">{description}</small> : null}
        </div>
        <div className="card-body">{children}</div>
    </div>
);

const AdminModell = () => {
    const navigate = useStepNavigate();
    const { modelId } = useParams();
    const location = useLocation();
    const navigationState = location.state || {};
    const saveMessageTimeoutRef = useRef(null);

    const [adminModels, setAdminModels] = useState(
        (Array.isArray(navigationState.adminModels) ? navigationState.adminModels : []).filter((model) => !isModelHiddenInAdminList(model))
    );
    const [modellConfigs, setModellConfigs] = useState(Array.isArray(navigationState.modellConfigs) ? navigationState.modellConfigs : []);
    const [allOptions, setAllOptions] = useState(navigationState.allOptions && !Array.isArray(navigationState.allOptions) ? navigationState.allOptions : {});
    const [currentConfig, setCurrentConfig] = useState(null);
    const [modelConfigSaveId, setModelConfigSaveId] = useState(1);
    const [stemHeightDraft, setStemHeightDraft] = useState("");
    const [colorGroupsDraft, setColorGroupsDraft] = useState(Array.from({ length: COLOR_GROUP_COUNT }, () => []));
    const [newColorGroupEntries, setNewColorGroupEntries] = useState(Array.from({ length: COLOR_GROUP_COUNT }, () => ""));
    const [savingKeys, setSavingKeys] = useState({});
    const [saveStatusMessage, setSaveStatusMessage] = useState("");
    const [saveStatusType, setSaveStatusType] = useState("success");
    const [saveErrorModal, setSaveErrorModal] = useState({ isOpen: false, message: "" });
    const [hideModelModalOpen, setHideModelModalOpen] = useState(false);
    const [isHideModelSubmitting, setIsHideModelSubmitting] = useState(false);
    const [loading, setLoading] = useState(!(Array.isArray(navigationState.adminModels) && Array.isArray(navigationState.modellConfigs) && navigationState.allOptions));
    const [error, setError] = useState(null);

    useEffect(() => {
        return () => {
            if (saveMessageTimeoutRef.current) {
                clearTimeout(saveMessageTimeoutRef.current);
                saveMessageTimeoutRef.current = null;
            }
        };
    }, []);

    const showTransientSaveMessage = (message, type) => {
        if (saveMessageTimeoutRef.current) {
            clearTimeout(saveMessageTimeoutRef.current);
        }

        setSaveStatusMessage(message);
        setSaveStatusType(type);

        saveMessageTimeoutRef.current = setTimeout(() => {
            setSaveStatusMessage("");
            saveMessageTimeoutRef.current = null;
        }, 2500);
    };

    useEffect(() => {
        const fetchData = async () => {
            const hasNavigationData = Array.isArray(navigationState.adminModels) && Array.isArray(navigationState.modellConfigs) && navigationState.allOptions;
            if (hasNavigationData) {
                setLoading(false);
                setError(null);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const [modelConfigsResponse, allOptionsResponse, adminModelsResponse] = await Promise.all([
                    dataService.getModelConfigs(),
                    dataService.getAllOptions(),
                    dataService.getAdminModels(),
                ]);

                const modelConfigList = normalizeListPayload(modelConfigsResponse);
                const optionGroups = normalizeObjectPayload(allOptionsResponse);
                const modelList = normalizeListPayload(adminModelsResponse).filter((model) => !isModelHiddenInAdminList(model));

                setModellConfigs(modelConfigList);
                setAllOptions(optionGroups);
                setAdminModels(modelList);
                showSuccessToast("Modell szerkeszto adatok sikeresen betoltve!");
            } catch (err) {
                console.error("Admin modell adatok fetch error:", err);
                setError(err.message || "Hiba tortent az adatok betoltese soran");
                showErrorToast("Hiba tortent az adatok betoltese soran: " + (err.message || err));
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [modelId, navigationState.adminModels, navigationState.modellConfigs, navigationState.allOptions]);

    const selectedModel = useMemo(() => {
        return adminModels.find((model) => String(getFirstDefinedValue(model, ["szam"], "")) === String(modelId)) || null;
    }, [adminModels, modelId]);

    useEffect(() => {
        const matchedConfig = selectedModel ? findMatchingConfig(selectedModel, modellConfigs) : null;
        const baseConfig = matchedConfig ? cloneValue(matchedConfig) : { mmelimodel: modelId };
        setCurrentConfig(baseConfig);
        setModelConfigSaveId(matchedConfig?.szam ?? 1);
        setStemHeightDraft(baseConfig?.max_stem_height === null || baseConfig?.max_stem_height === undefined ? "" : String(baseConfig.max_stem_height));
        setColorGroupsDraft(parseColorGroupsValue(baseConfig?.color_groups));
        setNewColorGroupEntries(Array.from({ length: COLOR_GROUP_COUNT }, () => ""));
    }, [selectedModel, modellConfigs, modelId]);

    const accessoryComponents = useMemo(() => [...(Array.isArray(allOptions?.components) ? allOptions.components : [])].sort((left, right) => Number(left?.aidfinance_szam || 0) - Number(right?.aidfinance_szam || 0)), [allOptions]);
    const mountingTypes = useMemo(() => [...(Array.isArray(allOptions?.mounting_types) ? allOptions.mounting_types : [])].sort((left, right) => Number(left?.mounting_type_szam || 0) - Number(right?.mounting_type_szam || 0)), [allOptions]);
    const shoeHornModels = useMemo(() => [...(Array.isArray(allOptions?.shoe_horn_models) ? allOptions.shoe_horn_models : [])].sort((left, right) => Number(left?.shoe_horn_model_szam || 0) - Number(right?.shoe_horn_model_szam || 0)), [allOptions]);
    const cornerTypes = useMemo(() => [...(Array.isArray(allOptions?.corner_types) ? allOptions.corner_types : [])].sort((left, right) => Number(left?.corner_type_szam || 0) - Number(right?.corner_type_szam || 0)), [allOptions]);
    const linerMaterials = useMemo(() => [...(Array.isArray(allOptions?.linermaterials) ? allOptions.linermaterials : [])].sort((left, right) => Number(left?.liner_material_szam || 0) - Number(right?.liner_material_szam || 0)), [allOptions]);

    const aidfinanceStateMap = useMemo(() => {
        const entries = parseJsonArray(currentConfig?.sh_med_tps_dsllwd);
        return new Map(entries.map((entry) => [String(entry?.aidfinance_code || "").trim().toUpperCase(), String(entry?.is_allowed || "").trim().toLowerCase()]));
    }, [currentConfig]);

    const mountingTypeStateMap = useMemo(() => {
        const entries = parseJsonArray(currentConfig?.mnt_typs_avlb);
        return new Map(entries.map((entry) => [String(entry?.mounting_type_szam || "").trim(), Number(entry?.is_allowed)]));
    }, [currentConfig]);

    const shoeHornStateMap = useMemo(() => {
        const entries = parseJsonArray(currentConfig?.shoe_horn_avlb);
        return new Map(entries.map((entry) => [String(entry?.shoe_horn_model_szam || "").trim(), String(entry?.optional || "").trim().toLowerCase()]));
    }, [currentConfig]);

    const cornerTypeStateMap = useMemo(() => {
        const entries = parseJsonArray(currentConfig?.corner_types_avlb);
        return new Map(entries.map((entry) => [String(entry?.corner_type_szam || "").trim(), String(entry?.optional || "").trim().toLowerCase()]));
    }, [currentConfig]);

    const blockedComponentIds = useMemo(() => normalizeComponentBlockedIds(currentConfig?.components_dsllwd), [currentConfig]);
    const blockedLinerMaterialIds = useMemo(() => normalizeBlockedIdsList(currentConfig?.linrmterls_dsllwd), [currentConfig]);

    const applyConfigChange = async (propertyName, nextValue) => {
        const previousValue = cloneValue(currentConfig?.[propertyName]);
        const previousModelConfigSaveId = modelConfigSaveId;
        const modelNumber = getFirstDefinedValue(selectedModel, ["szam"], modelId);

        setCurrentConfig((previousConfig) => ({
            ...(previousConfig || {}),
            mmelimodel: previousConfig?.mmelimodel ?? modelNumber,
            [propertyName]: cloneValue(nextValue),
        }));
        setSavingKeys((previous) => ({ ...previous, [propertyName]: true }));

        try {
            const response = await dataService.setModelConfig({
                mmelimodel: modelNumber,
                mmelimodelconfig: modelConfigSaveId,
                property_name: propertyName,
                property_value: nextValue,
            });

            if (isErrorStatusResponse(response)) {
                throw new Error("A szerver status=error válasszal utasította el a módosítást." + (response?.data?.message ? " Üzenet: " + response.data.message : ""));
            }

            const nextModelConfigId = extractModelConfigIdFromResponse(response, modelConfigSaveId);
            if (nextModelConfigId !== undefined && nextModelConfigId !== null && nextModelConfigId !== "") {
                setModelConfigSaveId(nextModelConfigId);
                setCurrentConfig((previousConfig) => ({
                    ...(previousConfig || {}),
                    szam: nextModelConfigId,
                }));
            }
            showTransientSaveMessage("Sikeres mentés!", "success");
        } catch (err) {
            setModelConfigSaveId(previousModelConfigSaveId);
            setCurrentConfig((previousConfig) => ({
                ...(previousConfig || {}),
                mmelimodel: previousConfig?.mmelimodel ?? modelNumber,
                [propertyName]: previousValue,
            }));
            setSaveErrorModal({
                isOpen: true,
                message: "A mentés sikertelen volt, az előző érték vissza lett állítva. " + (err.message || ""),
            });
            showErrorToast("A változás mentése sikertelen: " + (err.message || err));
        } finally {
            setSavingKeys((previous) => {
                const nextKeys = { ...previous };
                delete nextKeys[propertyName];
                return nextKeys;
            });
        }
    };

    const handleAidfinanceChange = (aidfinanceCode, stateValue) => {
        const nextEntries = mergeArrayEntry(parseJsonArray(currentConfig?.sh_med_tps_dsllwd), "aidfinance_code", aidfinanceCode, {
            aidfinance_code: aidfinanceCode,
            is_allowed: stateValue,
        });
        applyConfigChange("sh_med_tps_dsllwd", nextEntries);
    };

    const handleMountingTypeChange = (mountingTypeId, stateValue) => {
        const nextEntries = mergeArrayEntry(parseJsonArray(currentConfig?.mnt_typs_avlb), "mounting_type_szam", mountingTypeId, {
            mounting_type_szam: Number(mountingTypeId),
            is_allowed: Number(stateValue),
        });
        applyConfigChange("mnt_typs_avlb", nextEntries);
    };

    const handleShoeHornChange = (shoeHornModelId, stateValue) => {
        const nextEntries = mergeArrayEntry(parseJsonArray(currentConfig?.shoe_horn_avlb), "shoe_horn_model_szam", shoeHornModelId, {
            shoe_horn_model_szam: Number(shoeHornModelId),
            optional: stateValue,
        });
        applyConfigChange("shoe_horn_avlb", nextEntries);
    };

    const handleCornerTypeChange = (cornerTypeId, stateValue) => {
        const nextEntries = mergeArrayEntry(parseJsonArray(currentConfig?.corner_types_avlb), "corner_type_szam", cornerTypeId, {
            corner_type_szam: Number(cornerTypeId),
            optional: stateValue,
        });
        applyConfigChange("corner_types_avlb", nextEntries);
    };

    const handleComponentStateChange = (componentId, isAllowed) => {
        const nextBlockedIds = isAllowed ? removeSetValue(blockedComponentIds, componentId) : addSetValue(blockedComponentIds, componentId);
        applyConfigChange("components_dsllwd", nextBlockedIds);
    };

    const handleLinerMaterialStateChange = (materialId, isAllowed) => {
        const nextBlockedIds = isAllowed ? removeSetValue(blockedLinerMaterialIds, materialId) : addSetValue(blockedLinerMaterialIds, materialId);
        applyConfigChange("linrmterls_dsllwd", nextBlockedIds);
    };

    const handleSpecialNeedChange = (fieldName, nextValue) => {
        applyConfigChange(fieldName, Number(nextValue));
    };

    const handleStemHeightSave = () => {
        const trimmedValue = String(stemHeightDraft ?? "").trim();
        if (trimmedValue !== "") {
            const numericValue = Number(trimmedValue);
            if (Number.isNaN(numericValue) || !Number.isInteger(numericValue)) {
                setSaveErrorModal({
                    isOpen: true,
                    message: "A max. szármagasság csak egész szám lehet.",
                });
                return;
            }
        }

        const nextValue = toStemHeightValue(stemHeightDraft);
        applyConfigChange("max_stem_height", nextValue);
    };

    const handleStemHeightClear = () => {
        setStemHeightDraft("");
        applyConfigChange("max_stem_height", null);
    };

    const handleNewColorGroupEntryChange = (groupIndex, nextValue) => {
        setNewColorGroupEntries((previousEntries) =>
            previousEntries.map((entryValue, index) => (index === groupIndex ? nextValue : entryValue))
        );
    };

    const handleAddColorGroupEntry = (groupIndex) => {
        const nextEntry = String(newColorGroupEntries[groupIndex] ?? "").trim();
        if (nextEntry === "") {
            return;
        }

        setColorGroupsDraft((previousGroups) =>
            previousGroups.map((entries, index) => (index === groupIndex ? [...entries, nextEntry] : entries))
        );
        setNewColorGroupEntries((previousEntries) =>
            previousEntries.map((entryValue, index) => (index === groupIndex ? "" : entryValue))
        );
    };

    const handleRemoveColorGroupEntry = (groupIndex, entryIndex) => {
        setColorGroupsDraft((previousGroups) =>
            previousGroups.map((entries, index) =>
                index === groupIndex ? entries.filter((_, currentEntryIndex) => currentEntryIndex !== entryIndex) : entries
            )
        );
    };

    const handleColorGroupsSave = () => {
        const normalizedGroups = normalizeColorGroupsDraft(colorGroupsDraft);
        const nextValue = toColorGroupsValue(normalizedGroups);

        setColorGroupsDraft(normalizedGroups);
        applyConfigChange("color_groups", nextValue);
    };

    const handleBackToList = () => {
        navigate("/Admin/ModellKezelo");
    };

    const handleBackToAdmin = () => {
        navigate("/Admin");
    };

    const handleOpenHideModelModal = () => {
        setHideModelModalOpen(true);
    };

    const handleCloseHideModelModal = () => {
        if (isHideModelSubmitting) {
            return;
        }
        setHideModelModalOpen(false);
    };

    const handleHideModelConfirm = async () => {
        if (!selectedModel || isHideModelSubmitting) {
            return;
        }

        const modelNumber = getFirstDefinedValue(selectedModel, ["szam"], null);
        if (modelNumber === null || modelNumber === undefined || modelNumber === "") {
            showErrorToast("A modell száma nem elérhető, ezért nem lehet elrejteni a listából.");
            setHideModelModalOpen(false);
            return;
        }

        setIsHideModelSubmitting(true);
        try {
            await dataService.hideAdminModel({
                model_szam: modelNumber,
                hide_in_list: 1,
            });

            showSuccessToast("A modell elrejtve az admin listából.");
            navigate("/Admin/ModellKezelo");
        } catch (err) {
            showErrorToast("A modell elrejtése sikertelen: " + (err.message || err));
        } finally {
            setIsHideModelSubmitting(false);
            setHideModelModalOpen(false);
        }
    };

    if (loading || currentConfig === null) {
        return (
            <TablerLayout title="Admin modell">
                <div className="text-center py-5">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Betöltés...</span>
                    </div>
                    <p className="mt-3 mb-0">Modell szerkesztő betöltése...</p>
                </div>
            </TablerLayout>
        );
    }

    if (error) {
        return (
            <TablerLayout title="Admin modell">
                <div className="alert alert-danger">
                    <h4>Hiba történt</h4>
                    <p className="mb-3">{error}</p>
                    <div className="d-flex gap-2">
                        <button type="button" className="btn btn-secondary" onClick={handleBackToList}>
                            Vissza a modell listához
                        </button>
                        <button type="button" className="btn btn-outline-secondary" onClick={handleBackToAdmin}>
                            Vissza az admin oldalra
                        </button>
                    </div>
                </div>
            </TablerLayout>
        );
    }

    if (!selectedModel) {
        return (
            <TablerLayout title="Admin modell" fullWidth hideHeader>
                <div className="row justify-content-center">
                    <div className="col-12" style={{ maxWidth: "90vw" }}>
                        <div className="alert alert-warning mt-4">
                            <h4>A keresett modell nem található</h4>
                            <div className="d-flex gap-2 mt-3">
                                <button type="button" className="btn btn-secondary" onClick={handleBackToList}>
                                    Vissza a modell listához
                                </button>
                                <button type="button" className="btn btn-outline-secondary" onClick={handleBackToAdmin}>
                                    Vissza az admin oldalra
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </TablerLayout>
        );
    }

    return (
        <TablerLayout title="Admin modell" fullWidth hideHeader layoutStatusMessage={saveStatusMessage} layoutStatusType={saveStatusType}>
            <div className="row justify-content-center">
                <div className="col-12" style={{ maxWidth: "90vw" }}>
                    <div className="card shadow-sm">
                        <div className="card-header d-flex justify-content-between align-items-start gap-3 flex-wrap">
                            <div>
                                <h2 className="card-title mb-1">Modell konfigurációja</h2>
                                <div className="small text-muted">
                                    {getModelDisplayName(selectedModel)} / {getModelDisplayCode(selectedModel)} / #{getFirstDefinedValue(selectedModel, ["szam"], "?")} / #{modelConfigSaveId}
                                </div>
                            </div>
                            <div className="d-flex gap-2 flex-wrap">
                                <button type="button" className="btn btn-danger" onClick={handleOpenHideModelModal} disabled={isHideModelSubmitting}>
                                    Elrejtés az admin felületen a listából
                                </button>
                                <button type="button" className="btn btn-secondary" onClick={handleBackToList}>
                                    Vissza a modell listához
                                </button>
                                <button type="button" className="btn btn-outline-secondary" onClick={handleBackToAdmin}>
                                    Vissza az admin oldalra
                                </button>
                            </div>
                        </div>
                        <div className="card-body">
                            <div className="row g-4">
                                <div className="col-12">
                                    <SectionCard title="C0, C2-C5" description="sh_med_tps_dsllwd JSON konfiguráció mezői">
                                        <div className="row g-3">
                                            {AIDFINANCE_COLUMNS.map((aidfinanceCode) => (
                                                <div key={aidfinanceCode} className="col-sm-6 col-lg-4 col-xl-2">
                                                    <div className="border p-3 h-100">
                                                        <div className="fw-semibold mb-1">{aidfinanceCode}</div>
                                                        <ChoiceButtons
                                                            options={AIDFINANCE_STATE_OPTIONS}
                                                            value={aidfinanceStateMap.get(aidfinanceCode) || ""}
                                                            onChange={(nextValue) => handleAidfinanceChange(aidfinanceCode, nextValue)}
                                                            disabled={Boolean(savingKeys.sh_med_tps_dsllwd)}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </SectionCard>
                                </div>

                                <div className="col-12">
                                    <SectionCard title="Vényes tartozékok" description="components_dsllwd a fő szótár alapján">
                                        <div className="row g-3">
                                            {accessoryComponents.map((component) => {
                                                const componentId = String(component?.aidfinance_szam ?? "").trim();
                                                const isAllowed = componentId !== "" ? !blockedComponentIds.has(componentId) : true;

                                                return (
                                                    <div key={componentId || component.aidfinance_code} className="col-sm-6 col-lg-4 col-xl-2">
                                                        <div className="border p-3 h-100">
                                                            <div className="fw-semibold">{component.aidfinance_code}</div>
                                                            <div className="small text-muted mb-1">{component.aidfinance_national}</div>
                                                            <ChoiceButtons
                                                                options={BLOCKING_STATE_OPTIONS}
                                                                value={isAllowed ? 1 : 0}
                                                                onChange={(nextValue) => handleComponentStateChange(componentId, Number(nextValue) === 1)}
                                                                disabled={Boolean(savingKeys.components_dsllwd)}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </SectionCard>
                                </div>

                                <div className="col-12">
                                    <SectionCard title="Rögzítés típus" description="mnt_typs_avlb JSON konfiguráció mezői">
                                        <div className="row g-3">
                                            {mountingTypes.map((mountingType) => {
                                                const mountingTypeId = String(mountingType?.mounting_type_szam ?? "").trim();
                                                const currentValue = mountingTypeStateMap.has(mountingTypeId) ? mountingTypeStateMap.get(mountingTypeId) : "";

                                                return (
                                                    <div key={mountingTypeId} className="col-sm-6 col-lg-4 col-xl-2">
                                                        <div className="border p-3 h-100">
                                                            <div className="fw-semibold mb-2">{mountingType.mounting_type_name}</div>
                                                            <ChoiceButtons
                                                                options={BOOLEAN_STATE_OPTIONS}
                                                                value={currentValue}
                                                                onChange={(nextValue) => handleMountingTypeChange(mountingTypeId, nextValue)}
                                                                disabled={Boolean(savingKeys.mnt_typs_avlb)}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </SectionCard>
                                </div>

                                <div className="col-12">
                                    <SectionCard title="Orrforma" description="shoe_horn_avlb JSON konfiguráció mezői">
                                        <div className="row g-3">
                                            {shoeHornModels.map((shoeHornModel) => {
                                                const shoeHornModelId = String(shoeHornModel?.shoe_horn_model_szam ?? "").trim();

                                                return (
                                                    <div key={shoeHornModelId} className="col-sm-6 col-lg-4 col-xl-2">
                                                        <div className="border p-3 h-100">
                                                            <div className="fw-semibold mb-2">{shoeHornModel.shoe_horn_model_name}</div>
                                                            <ChoiceButtons
                                                                options={OPTIONAL_STATE_OPTIONS}
                                                                value={shoeHornStateMap.get(shoeHornModelId) || ""}
                                                                onChange={(nextValue) => handleShoeHornChange(shoeHornModelId, nextValue)}
                                                                disabled={Boolean(savingKeys.shoe_horn_avlb)}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </SectionCard>
                                </div>

                                <div className="col-12">
                                    <SectionCard title="Talp" description="corner_types_avlb JSON konfiguráció mezői">
                                        <div className="row g-3">
                                            {cornerTypes.map((cornerType) => {
                                                const cornerTypeId = String(cornerType?.corner_type_szam ?? "").trim();

                                                return (
                                                    <div key={cornerTypeId} className="col-sm-6 col-lg-4 col-xl-2">
                                                        <div className="border p-3 h-100">
                                                            <div className="fw-semibold mb-2">{cornerType.corner_type_name}</div>
                                                            <ChoiceButtons
                                                                options={OPTIONAL_STATE_OPTIONS}
                                                                value={cornerTypeStateMap.get(cornerTypeId) || ""}
                                                                onChange={(nextValue) => handleCornerTypeChange(cornerTypeId, nextValue)}
                                                                disabled={Boolean(savingKeys.corner_types_avlb)}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </SectionCard>
                                </div>

                                <div className="col-12">
                                    <SectionCard title="Bélés típus" description="linrmterls_dsllwd a fő szótár alapján">
                                        <div className="row g-3">
                                            {linerMaterials.map((linerMaterial) => {
                                                const materialId = String(linerMaterial?.liner_material_szam ?? "").trim();
                                                const isAllowed = materialId !== "" ? !blockedLinerMaterialIds.has(materialId) : true;

                                                return (
                                                    <div key={materialId} className="col-sm-6 col-lg-4 col-xl-3">
                                                        <div className="border p-3 h-100">
                                                            <div className="fw-semibold mb-2">{linerMaterial.liner_material_name}</div>
                                                            <ChoiceButtons
                                                                options={BLOCKING_STATE_OPTIONS}
                                                                value={isAllowed ? 1 : 0}
                                                                onChange={(nextValue) => handleLinerMaterialStateChange(materialId, Number(nextValue) === 1)}
                                                                disabled={Boolean(savingKeys.linrmterls_dsllwd)}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </SectionCard>
                                </div>

                                <div className="col-12 col-xl-8">
                                    <SectionCard title="Speciális igények" description="Külön kapcsolható boolean mezők">
                                        <div className="row g-3">
                                            {SPECIAL_NEED_COLUMNS.map((specialNeed) => (
                                                <div key={specialNeed.key} className="col-sm-6 col-lg-4">
                                                    <div className="border p-3 h-100">
                                                        <div className="fw-semibold mb-2">{specialNeed.label}</div>
                                                        <ChoiceButtons
                                                            options={BOOLEAN_STATE_OPTIONS}
                                                            value={Number(currentConfig?.[specialNeed.key] || 0)}
                                                            onChange={(nextValue) => handleSpecialNeedChange(specialNeed.key, nextValue)}
                                                            disabled={Boolean(savingKeys[specialNeed.key])}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </SectionCard>
                                </div>

                                <div className="col-12 col-xl-4">
                                    <SectionCard title="Max. szármagasság" description="Érték megadása külön mentési gombbal">
                                        <div className="d-flex flex-column gap-3">

                                            <FloatingLabelInput
                                                label="Szármagasság (cm)"
                                                id="szarmagassag"
                                                name="szarmagassag"
                                                type="number"
                                                step="1"
                                                max="12"
                                                addon="cm"
                                                // fontSize={12}
                                                value={stemHeightDraft}
                                                onChange={(event) => setStemHeightDraft(event.target.value)}
                                                placeholder="Üresen hagyva törlés"
                                            />

                                            <div className="d-flex gap-2 flex-wrap">
                                                <button type="button" className="btn btn-primary" onClick={handleStemHeightSave} disabled={Boolean(savingKeys.max_stem_height)}>
                                                    Mentés
                                                </button>
                                                <button type="button" className="btn btn-outline-secondary" onClick={handleStemHeightClear} disabled={Boolean(savingKeys.max_stem_height)}>
                                                    Törlés
                                                </button>
                                            </div>
                                        </div>
                                    </SectionCard>
                                </div>

                                <div className="col-12">
                                    <SectionCard title="Szín csoportok" description="color_groups szöveg felbontása 3 oszlopra, külön mentéssel">
                                        <div className="row g-3">
                                            {Array.from({ length: COLOR_GROUP_COUNT }, (_, groupIndex) => {
                                                const entries = Array.isArray(colorGroupsDraft[groupIndex]) ? colorGroupsDraft[groupIndex] : [];

                                                return (
                                                    <div key={`color-group-${groupIndex}`} className="col-12 col-md-6 col-xl-4">
                                                        <div className="border p-3 h-100 d-flex flex-column gap-2">
                                                            <div className="fw-semibold">Szín {groupIndex + 1}</div>

                                                            <div className="d-flex flex-column gap-2">
                                                                {entries.map((entry, entryIndex) => (
                                                                    <div key={`color-group-${groupIndex}-entry-${entryIndex}`} className="d-flex align-items-center gap-2">
                                                                        <div className="form-control bg-light">{entry}</div>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-danger"
                                                                            onClick={() => handleRemoveColorGroupEntry(groupIndex, entryIndex)}
                                                                            disabled={Boolean(savingKeys.color_groups)}
                                                                            aria-label={`Szín ${groupIndex + 1} elem törlése`}
                                                                        >
                                                                            x
                                                                        </button>
                                                                    </div>
                                                                ))}

                                                                {entries.length === 0 ? <div className="small text-muted">Nincs bejegyzés.</div> : null}
                                                            </div>

                                                            <div className="d-flex align-items-center gap-2 mt-1">
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="Új elem"
                                                                    value={newColorGroupEntries[groupIndex] || ""}
                                                                    onChange={(event) => handleNewColorGroupEntryChange(groupIndex, event.target.value)}
                                                                    onKeyDown={(event) => {
                                                                        if (event.key === "Enter") {
                                                                            event.preventDefault();
                                                                            handleAddColorGroupEntry(groupIndex);
                                                                        }
                                                                    }}
                                                                    disabled={Boolean(savingKeys.color_groups)}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-primary"
                                                                    onClick={() => handleAddColorGroupEntry(groupIndex)}
                                                                    disabled={Boolean(savingKeys.color_groups)}
                                                                >
                                                                    +
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <div className="d-flex gap-2 flex-wrap mt-3">
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={handleColorGroupsSave}
                                                disabled={Boolean(savingKeys.color_groups)}
                                            >
                                                Mentés
                                            </button>
                                        </div>
                                    </SectionCard>
                                </div>
                            </div>
                        </div>
                        <div className="card-footer d-flex justify-content-between gap-2 flex-wrap">
                            <button type="button" className="btn btn-secondary" onClick={handleBackToList}>
                                Vissza a modell listához
                            </button>
                            <button type="button" className="btn btn-outline-secondary" onClick={handleBackToAdmin}>
                                Vissza az admin oldalra
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <Modal
                isOpen={hideModelModalOpen}
                onClose={handleCloseHideModelModal}
                onConfirm={handleHideModelConfirm}
                title="Modell elrejtése"
                type="danger"
                confirmText={isHideModelSubmitting ? "Folyamatban..." : "Igen, elrejtem"}
                cancelText="Mégsem"
                confirmDisabled={isHideModelSubmitting}
            >
                <div>
                    Biztosan elrejted ezt a modellt az admin listából? Ezután nem fog megjelenni a modell kezelő listában.
                </div>
            </Modal>
            <Modal
                isOpen={saveErrorModal.isOpen}
                onClose={() => setSaveErrorModal({ isOpen: false, message: "" })}
                onConfirm={() => setSaveErrorModal({ isOpen: false, message: "" })}
                title="Mentési hiba"
                type="danger"
                confirmText="OK"
                showCancel={false}
            >
                <div>{saveErrorModal.message}</div>
            </Modal>
        </TablerLayout>
    );
};

export default AdminModell;